import { errorLog, notify } from '@/utils'
import { useApp } from './app'
import { createStore } from './util'
import {
  User,
  Point,
  ApiPointResponse,
  PointsErrorResponse,
  UserRole,
  PointHistoryItem,
} from '@/types'
import { User as PrivyUser } from '@privy-io/react-auth'
import { AxiosError } from 'axios'

// Point calculation matching server-side logic (ping-worker/src/config.rs + types.rs)
const BASE_POINTS_PER_PING = 20 // Matches server BASE_POINTS_PER_PING default

const getPointsPerPing = (role: UserRole): number => {
  // Server calculates: base_points * role_multiplier
  switch (role) {
    case UserRole.Desktop:
      return BASE_POINTS_PER_PING * 3 // 20 * 3 = 60 points
    default:
      return BASE_POINTS_PER_PING * 1 // 20 * 1 = 20 points
  }
}

interface UserStore {
  loading: boolean
  setLoading: (loading: boolean) => void
  token: string | null
  setToken: (token: string | null) => void
  isAuth: boolean
  user: User | null // our app user (from our API/DB)
  privyUser: PrivyUser | null // identity from Privy SDK (emails, wallets, etc.)
  clearUser: () => void
  auth: (redirect?: string, privyAccessToken?: string) => Promise<void>
  logout: () => Promise<void>
  getTotalEarnings: () => Promise<Point>
  getPoints: (startDate?: string, endDate?: string) => Promise<Point[]>
  getPointHistory: (startDate?: string, endDate?: string) => Promise<Point[]>
  getStreak: () => Promise<number>
  updateEmail: (email: string) => Promise<void>
}

export const useUser = createStore<UserStore>(
  'user',
  (set, get) => ({
    loading: false,
    setLoading: (loading) => set({ loading }),
    token: null,
    setToken: (token) => set({ token }),
    isAuth: false,
    user: null,
    privyUser: null,
    clearUser: () => set({ user: null, privyUser: null }),
    auth: async (redirect, privyAccessToken) => {
      useApp.getState().setLoading(true)

      try {
        // Use the provided Privy token or get from localStorage as fallback
        const token = privyAccessToken || window.localStorage.getItem('privy-token')

        if (!token) {
          throw new Error('No authentication token available')
        }

        // Save the Privy token to localStorage for future use as fallback
        if (privyAccessToken) {
          window.localStorage.setItem('privy-token', privyAccessToken)
        }

        // Update our store with the token
        get().setToken(token)

        const http = useApp.getState().http
        console.log('Using token:', token?.substring(0, 20) + '...')

        const response = await http.get('/auth?role=explorer')

        console.log('response from auth', { response })

        set({
          user: response.data.user,
          privyUser: response.data.privy_user,
          isAuth: true,
        })

        if (redirect) {
          // for local redirects use dom redirect
          const isLocal = window.location.pathname.startsWith('/')

          if (isLocal) {
            window.history.pushState({}, '', redirect)
          } else {
            window.location.href = redirect
          }
        }
      } catch (err) {
        console.error('Auth error:', err)

        // Clear any stored tokens on auth failure
        window.localStorage.removeItem('privy-token')
        get().setToken(null)
        set({ isAuth: false, user: null, privyUser: null })

        // Provide specific error messages
        const error = err as AxiosError
        if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
          throw new AxiosError('Server connection failed. Please try again.')
        } else if (error?.response?.status === 401) {
          throw new AxiosError('Authentication failed. Please try again.')
        } else {
          throw new AxiosError('Login failed. Please try again.')
        }
      } finally {
        useApp.getState().setLoading(false)
      }
    },
    // NOTE: we need to logout from privy before clearing the user state otherwise the user will be logged in automatically
    logout: async () => {
      get().setLoading(true)
      get().clearUser()
      get().setToken(null)
      window.localStorage.removeItem('privy-token')
      set({ isAuth: false })

      get().setLoading(false)
    },
    getTotalEarnings: async (): Promise<Point> => {
      const token = get().token
      const http = useApp.getState().http

      try {
        if (!token) throw 'No token found'

        const response = await http.get<ApiPointResponse | PointsErrorResponse>('/point')

        if (!response.data) {
          throw 'No data returned from server'
        }

        if ('error' in response.data) {
          throw response.data.error
        }

        // API returns { points: i64, referral_points: i64, created_at, updated_at } but frontend expects { value: number, created_at }
        return {
          value: response.data.points + response.data.referral_points,
          created_at: response.data.created_at,
        }
      } catch (error) {
        errorLog(error)
        return { value: 0, created_at: new Date().toISOString() }
      }
    },
    getPoints: async (startDate?: string, endDate?: string): Promise<Point[]> => {
      const token = get().token
      const http = useApp.getState().http
      try {
        if (!token) throw 'No token found'

        const params = new URLSearchParams()
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)

        const queryString = params.toString()
        const url = `/ping/total${queryString ? `?${queryString}` : ''}`

        const response = await http.get<
          { total: number; by_role: Array<{ role: UserRole; count: number }> } | PointsErrorResponse
        >(url)

        if (!response.data) {
          throw 'No data returned from server'
        }

        if ('error' in response.data) {
          throw response.data.error
        }

        // Calculate total points from the ping counts by role
        let totalPoints = 0
        for (const roleCount of response.data.by_role) {
          const pointsPerPing = getPointsPerPing(roleCount.role)
          totalPoints += roleCount.count * pointsPerPing
        }

        // Return a single point entry with the aggregated total from /ping/total endpoint
        // Changed from fetching individual pings via /ping to using aggregated counts via /ping/total
        // This is more efficient (less data transfer) and reduces client-side processing
        // The lastDayPoints in useDashboardData hook receives this but is currently unused in the UI
        return [
          {
            value: totalPoints,
            created_at: new Date().toISOString(),
          },
        ]
      } catch (error) {
        errorLog(error)
        return []
      }
    },
    getPointHistory: async (startDate?: string, endDate?: string): Promise<Point[]> => {
      const token = get().token
      const http = useApp.getState().http
      try {
        if (!token) throw 'No token found'

        const params = new URLSearchParams()
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)

        const queryString = params.toString()
        const url = `/point/history${queryString ? `?${queryString}` : ''}`

        const response = await http.get<PointHistoryItem[] | PointsErrorResponse>(url)

        if (!response.data) {
          throw 'No data returned from server'
        }

        if ('error' in response.data) {
          throw response.data.error
        }

        // Convert PointHistoryItem[] to Point[] to maintain compatibility
        const points: Point[] = response.data.map((item: PointHistoryItem) => ({
          value: item.points,
          created_at: item.created_at,
        }))

        return points
      } catch (error) {
        errorLog(error)
        return []
      }
    },
    getStreak: async (): Promise<number> => {
      const token = get().token
      const http = useApp.getState().http
      try {
        if (!token) throw 'No token found'

        const response = await http.get<{ currentStreak: number } | PointsErrorResponse>(
          '/ping/streak',
        )

        if (!response.data) {
          throw 'No data returned from server'
        }

        if ('error' in response.data) {
          throw response.data.error
        }

        return response.data.currentStreak
      } catch (error) {
        errorLog(error)
        return 0
      }
    },
    updateEmail: async (email: string): Promise<void> => {
      const token = get().token
      const http = useApp.getState().http
      try {
        if (!token) throw new Error('No token found')

        const response = await http.post<User>('/user/email', { email })

        if (!response.data) {
          throw new Error('No data returned from server')
        }

        // Update local user state with new email
        set({ user: response.data })
        notify.success('Email updated in database successfully')
      } catch (error) {
        errorLog(error)
        notify.error('Failed to update email in database')
        throw error
      }
    },
  }),
  true,
)
